import React, { Component, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import { renderableType } from '../proptypes/prop-types';
import './nimbusTooltip.scss';
import { buildClassNames, buildClassNameModifiers, px } from '../util/utils';

const blockClassName = 'tooltip';
const containerClassName = 'tooltip__container';
const textClassName = 'tooltip__text';

function debounce(func, delay) {
  let debounceTimer;
  const debounced = function() {
    const context = this;
    const args = arguments;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(context, args), delay);
  };
  debounced.cancel = function() {
    clearTimeout(debounceTimer);
  };
  return debounced;
}

export class NimbusTooltip extends Component {
  static propTypes = {
    /** @ignore */
    children: renderableType,
    /** @ignore */
    className: PropTypes.string,
    /** The ID given to the tooltip */
    id: PropTypes.string,
    /** The relative position of the tooltip */
    position: PropTypes.oneOf([
      'top', 'top-start', 'top-end',
      'right', 'right-start', 'right-end',
      'bottom', 'bottom-start', 'bottom-end',
      'left', 'left-start', 'left-end',
    ]),
    /** The content of the tooltip */
    text: PropTypes.string,
  };

  static defaultProps = { position: 'top' };

  constructor(props) {
    super(props);
    this.state = { open: false, contentStyle: undefined };
  }

  componentDidMount() {
    this.updateTooltipPosition();
  }

  updateTooltipPosition = () => {
    if (this.childContainer) {
      const childContainerRect = this.childContainer.getBoundingClientRect();
      let style = { willChange: 'transform' };
      let x = 0;
      let y = 0;
      let tooltipWidth = 0;
      let tooltipHeight = 0;
      if (this.tooltipRef) {
        tooltipWidth = this.tooltipRef.offsetWidth;
        tooltipHeight = this.tooltipRef.offsetHeight;
      }
      switch (this.props.position) {
        case 'left':
          x = `calc(-${px(tooltipWidth)} - .5rem)`;
          y = `calc(${px(childContainerRect.height / 2)} - ${px(tooltipHeight / 2)})`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'left-start':
          x = `calc(-${px(tooltipWidth)} - .5rem)`;
          y = '0px';
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'left-end':
          x = `calc(-${px(tooltipWidth)} - .5rem)`;
          y = `calc(${px(childContainerRect.height)} - ${px(tooltipHeight)})`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'right':
          x = `calc(${px(childContainerRect.width)} + .5rem)`;
          y = `calc(${px(childContainerRect.height / 2)} - ${px(tooltipHeight / 2)})`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'right-start':
          x = `calc(${px(childContainerRect.width)} + .5rem)`;
          y = '0px';
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'right-end':
          x = `calc(${px(childContainerRect.width)} + .5rem)`;
          y = `calc(${px(childContainerRect.height)} - ${px(tooltipHeight)})`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'bottom':
          x = `calc(${px(childContainerRect.width / 2)} - ${px(tooltipWidth / 2)})`;
          y = `calc(${px(childContainerRect.height)} + .5rem)`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'bottom-start':
          x = '0px';
          y = `calc(${px(childContainerRect.height)} + .5rem)`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'bottom-end':
          x = `calc(0px - (${px(tooltipWidth)} - ${px(childContainerRect.width)}))`;
          y = `calc(${px(childContainerRect.height)} + .5rem)`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'top-start':
          x = '0px';
          y = `calc(-${px(tooltipHeight)} - .5rem)`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'top-end':
          x = `calc(0px - (${px(tooltipWidth)} - ${px(childContainerRect.width)}))`;
          y = `calc(-${px(tooltipHeight)} - .5rem)`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
          break;
        case 'top':
        default:
          x = `calc(${px(childContainerRect.width / 2)} - ${px(tooltipWidth / 2)})`;
          y = `calc(-${px(tooltipHeight)} - .5rem)`;
          style = { ...style, transform: `translate3d(${x}, ${y}, 0px)` };
      }
      this.setState({ contentStyle: style });
    }
  }

  handleShow = debounce(() => {
    this.setState({ open: true }, this.updateTooltipPosition);
  }, 500);

  handleHide = () => {
    this.handleShow.cancel();
    this.setState({ open: false });
  };

  render() {
    const {
      children,
      className,
      id,
      position,
      text,
      ...other
    } = this.props;
    const { contentStyle, open } = this.state;
    return (
      <div
        className={buildClassNames(className, blockClassName)}
        onMouseOver={this.handleShow}
        onMouseOut={this.handleHide}
        onFocus={this.handleShow}
        onBlur={this.handleHide}
        ref={(n) => { this.childContainer = n; }}
        {...other}
      >
        {typeof this.props.children === 'string'
          ? <span aria-describedby={id}>{this.props.children}</span>
          : cloneElement(React.Children.only(children), { 'aria-describedby': id })
        }
        <CSSTransition
          classNames={containerClassName}
          in={open}
          timeout={150}
          mountOnEnter
          unmountOnExit
        >
          {() =>
            (
              <div
                role='tooltip'
                id={id}
                ref={(n) => { this.tooltipRef = n; }}
                className={containerClassName}
                style={contentStyle}
              >
                <div
                  className={buildClassNames(
                    buildClassNameModifiers(textClassName, position),
                    textClassName,
                  )}
                >
                  {text}
                </div>
              </div>
            )
          }
        </CSSTransition>
      </div>
    );
  }
}
